import {GameData} from "../../../server/data/GameData";
import {ModeTriggerPresentation} from "./ModeTriggerPresentation";
import {GameMode} from "@nolimitcity/slot-game/bin/flowcontrol/GameMode";
import {StateHandler} from "../../../../core/utils/StateHandler";
import {PREngineManager} from "../../../punkEngine/PREngineManager";
import {PREngineController} from "../../../punkEngine/PREngineController";
import {PunkSymbol} from "../../../punkEngine/symbol/PunkSymbol";
import {PunkRocker} from "../../../PunkRocker";

class RespinRestoreStates extends StateHandler {
    public static readonly RESPIN_START: string = "RESPIN_START";
    public static readonly RESPIN_MYSTERY_CHECK: string = "RESPIN_MYSTERY_CHECK";
    public static readonly RESPIN_MYSTERY_REVEAL: string = "RESPIN_MYSTERY_REVEAL";
    public static readonly RESPIN_DONE: string = "RESPIN_DONE";
}

export class RespinRestorePresentation extends ModeTriggerPresentation {

    protected gameData: GameData;
    protected playRespinRestore: boolean;
    protected mysterySymbolRevealed: string;
    protected activeSkullsCount: number;
    protected gameSize: number[];
    protected respinRestoreStates: RespinRestoreStates = new RespinRestoreStates();

    public checkForActivation(data: GameData): boolean {
        return data.isRestoreState && (data.nextMode === GameMode.RESPIN);
    }

    setGamedData(data: GameData): void {
        this.gameData = data;
        this.activeSkullsCount = data.raw.scatterPositions.filter(item => item > -1).length;
        this.gameSize = data.Reel.gameAreaSize;
        super.setGamedData(data);
    }

    preStart(): void {
        if (this.gameData.isRestoreState && (this.gameData.nextMode === GameMode.RESPIN)) {
            this.playRespinRestore = true;
            this.respinRestoreStates.set(RespinRestoreStates.RESPIN_START);
            PunkRocker.waysCounter.setValue(undefined, Math.pow(3, 6), 0);
        }
    }

    createPresentation(enableSkip: Function): void {
        if (this.playRespinRestore) {
            PunkRocker.keypadPlugin.setWin(this.gameData.raw.accumulatedRoundWin, true, this.gameData.Win.isWinBelowStake());
            switch (this.respinRestoreStates.value) {
                case RespinRestoreStates.RESPIN_START:
                    this.respinRestoreStates.set(RespinRestoreStates.RESPIN_MYSTERY_CHECK);
                    break;
                case RespinRestoreStates.RESPIN_MYSTERY_CHECK:
                    this.checkForMystery();
                    this.respinRestoreStates.set(RespinRestoreStates.RESPIN_MYSTERY_REVEAL);
                    break;
                case RespinRestoreStates.RESPIN_MYSTERY_REVEAL:
                    this.revealMysteries();
                    this.revealXWild();
                    this.respinRestoreStates.set(RespinRestoreStates.RESPIN_DONE);
                    break;
                default:
                    this.playRespinRestore = false;
                    break;
            }
        } else {
            super.createPresentation(enableSkip);
        }
    }

    private checkForMystery(): void {
        PREngineManager.RSWindow((reel: PREngineController, sym: PunkSymbol): void => {
            if (this.gameData.Reel.symbolSizes[reel.reelID][sym.posID[0]] > 1 && !(sym.isXWild)) {
                this.mysterySymbolRevealed = sym.symName;
                sym.setSymbol("M");
                const tlDuration: number = 0.2;

                const calcWays: number[] = this.gameData.Reel.calculateMultiWaysValues(this.gameData.mode);

                this.timeline.add([
                    PunkRocker.waysCounter.setValue(undefined, calcWays[reel.reelID], tlDuration),
                    reel.adjustExpandingSymbols(reel.reelID, sym.posID[0], 0.2, this.gameData.raw.symbolSizes)
                ], 0);
            }
        });
    }

    private revealMysteries(): void {
        PREngineManager.RSWindow((currReel: PREngineController, currSym: PunkSymbol): void => {
            if (!(currSym.isXWild)) {
                const reelId: number = currReel.reelID,
                    symId: number = currSym.posID[0],
                    allSymSizes: number[][] = this.gameData.Reel.symbolSizes,
                    currSymSize: number = allSymSizes[reelId][symId];

                if ((currSymSize > 1) && (symId >= 0)) {
                    this.timeline.add([
                        currSym!.shakeMultiBeforeMysteryTransform(this.mysterySymbolRevealed, currSymSize),
                        currSym!.doLighteningBeforeMysteryTransform(this.mysterySymbolRevealed, currSymSize)
                    ], 0);
                }
            }
        });

        // #364
        this.timeline.addLabel("doTransform");

        PREngineManager.RSWindow((currReel: PREngineController, currSym: PunkSymbol): void => {
            if (!(currSym.isXWild)) {
                const reelId: number = currReel.reelID,
                    symId: number = currSym.posID[0],
                    allSymSizes: number[][] = this.gameData.Reel.symbolSizes,
                    currSymSize: number = allSymSizes[reelId][symId];

                if ((currSymSize > 1) && (symId >= 0)) {
                    this.timeline.add(currSym!.transformMystery(this.mysterySymbolRevealed, currSymSize), "doTransform");

                    // #264
                    this.timeline.add(PunkRocker.checkAndApplyChalkDividerOnMysteryTransform(reelId, symId, currSym, PunkRocker.checkIfRevealedSymIsLow(this.mysterySymbolRevealed)), "doTransform+=0.2");
                }
            }
        });
    }

    private revealXWild(): void {
        PREngineManager.AllReels.forEach(currReel => {
            currReel.AllWindowSymbols.forEach(currSym => {
                if (currSym.isXWild) {
                    const reelId: number = currReel.reelID,
                        symId: number = currSym.posID[0],
                        allSymSizes: number[][] = this.gameData.Reel.symbolSizes,
                        currSymSize: number = allSymSizes[reelId][symId];

                    this.timeline.add(currSym!.changeXWildValue("XWILD", (currSymSize) + 3), 0);
                }
            });
        });
    }
}